import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NotificationsApi } from '@api/notifications.api';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { sentry } from 'sentry';
import { ConfirmDialogService } from '../../../../@fuse/components/confirm-dialog/confirm-dialog.service';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationsService {

  private subscriptionId: string = null;

  constructor(
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly translate: TranslateService,
    private readonly notificationsApi: NotificationsApi,
    private readonly confirmDialogService: ConfirmDialogService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public async refreshNotificationSubscription(): Promise<void> {
    await this.notificationsApi.syncUser();

    if (!this.isPushNotificationsAvailable()) {
      return;
    }

    const permission = await this.requestPermission();
    if (!permission) {
      return;
    }


    const subscription = await this.getSubscription();
    if (!subscription) {
      return;
    }

    this.subscriptionId = await this.notificationsApi.subscribe(subscription);
  }

  public async unsubscribe(): Promise<void> {
    if (this.subscriptionId) {
      await this.notificationsApi.unsubscribe(this.subscriptionId);
    }

    if (!this.isPushNotificationsAvailable()) {
      return;
    }

    const reg = await this.getServiceWorkerRegistration();

    if (!reg) {
      return;
    }

    const subscription = await reg.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
  }

  private async requestPermission(): Promise<boolean> {
    if (Notification.permission === 'denied' || localStorage.getItem('showNotificationDialogAgain') === 'false') {
      console.warn('The user has blocked notifications.');
      return false;
    }

    if (Notification.permission === 'default') {
      const result = await this.confirmDialogService.open({
        title: this.translate.instant('TOOLBAR.NOTIFICATIONS.TITLE'),
        okText: this.translate.instant('TOOLBAR.NOTIFICATIONS.CONFIRM_DIALOG.CONFIRM'),
        cancelText: this.translate.instant('TOOLBAR.NOTIFICATIONS.CONFIRM_DIALOG.CANCEL'),
        message: this.translate.instant('TOOLBAR.NOTIFICATIONS.MESSAGE'),
        checkboxMessage: {
          message: this.translate.instant('TOOLBAR.NOTIFICATIONS.CONFIRM_DIALOG.CHECKBOX_MESSAGE'),
          show: true
        },
      });

      if (!result || !result.confirmed) {
        console.warn('The user has rejected notifications.');
        if (result && result.checked) {
          localStorage.setItem('showNotificationDialogAgain', 'false');
        }
        return false;
      }
    }

    const permission = await Notification.requestPermission();

    return permission === 'granted';
  }

  private async getSubscription(): Promise<PushSubscription | null> {
    const registration = await this.getServiceWorkerRegistration();

    if (!registration) {
      return null;
    }

    const existingSubscription = await registration.pushManager.getSubscription();

    if (existingSubscription) {
      return existingSubscription;
    }

    return registration.pushManager
      .subscribe({
        userVisibleOnly: true,
        applicationServerKey: environment.VAPID_PUBLIC_KEY
      })
      .catch((err) => {
        console.warn('Error during getSubscription', err);
        sentry.captureException(err);
        return null;
      });
  }

  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    return navigator.serviceWorker.ready;
  }

  private isPushNotificationsAvailable(): boolean {
    return Boolean(navigator.serviceWorker && window['PushManager'] && environment.SERVICE_WORKER);
  }

}
